import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { 
  storefrontApiRequest, 
  CART_CREATE_MUTATION, 
  CART_LINES_ADD_MUTATION, 
  CART_LINES_REMOVE_MUTATION, 
  CART_QUERY,
  CART_DISCOUNT_CODES_UPDATE,
  CART_ATTRIBUTES_UPDATE,
  formatCheckoutUrl,
  isCartNotFoundError,
  type ShopifyProduct
} from '@/lib/shopify';
import { trackAddToCart, getOrCreateVisitorId, getFBC, getFBP, generateEventId, getTrafficSource, getEnrichmentEmail } from '@/lib/facebookPixel';
import { trackGA4AddToCart, getGA4ClientId, getGA4SessionId } from '@/lib/googleAnalytics';
import { getFixedPrice } from '@/lib/fixedPrices';
import { useLanguage, type Currency } from '@/lib/i18n';

// Map display currency to Shopify Markets country code
const CURRENCY_TO_COUNTRY: Record<Currency, string> = {
  USD: 'US',
  GBP: 'GB',
  AUD: 'AU',
  ILS: 'IL',
  CAD: 'CA',
  EUR: 'DE',
  NOK: 'NO',
  AED: 'AE',
};

export interface CartItem {
  lineId: string | null;
  product: ShopifyProduct;
  variantId: string;
  variantTitle: string;
  price: { amount: string; currencyCode: string };
  quantity: number;
  selectedOptions: Array<{ name: string; value: string }>;
}

interface CartStore {
  items: CartItem[];
  cartId: string | null;
  checkoutUrl: string | null;
  discountCode: string | null;
  discountedTotal: number | null;
  discountedSubtotal: number | null;
  discountedVariantIds: string[];
  isLoading: boolean;
  isSyncing: boolean;
  isCartOpen: boolean;
  addItem: (item: Omit<CartItem, 'lineId'>) => Promise<void>;
  updateQuantity: (variantId: string, quantity: number) => Promise<void>;
  removeItem: (variantId: string) => Promise<void>;
  clearCart: () => void;
  syncCart: () => Promise<void>;
  openCart: () => void;
  closeCart: () => void;
  getCheckoutUrl: () => string | null;
  getTotalItems: () => number;
  getTotalPrice: () => number;
  applyDiscount: (code: string) => Promise<boolean>;
  removeDiscount: () => Promise<void>;
}

// Build tracking attributes for cross-domain identity bridge (FB + GA4)
export function buildTrackingCartAttributes(): Array<{ key: string; value: string }> {
  let gender = '';
  let phone = '';
  try {
    const enrichment = typeof localStorage !== 'undefined' ? localStorage.getItem('_fb_user_enrichment') : null;
    if (enrichment) {
      const parsed = JSON.parse(enrichment);
      if (parsed?.ge) gender = parsed.ge;
      if (parsed?.ph) phone = parsed.ph;
    }
  } catch { /* ignore parse errors */ }

  return [
    { key: '_fb_external_id', value: getOrCreateVisitorId() },
    { key: '_fb_fbc', value: getFBC() || '' },
    { key: '_fb_fbp', value: getFBP() || '' },
    { key: '_fb_em', value: getEnrichmentEmail() },
    { key: '_fb_ph', value: phone },
    { key: '_fb_ge', value: gender },
    { key: '_ga_client_id', value: getGA4ClientId() || '' },
    { key: '_ga_session_id', value: getGA4SessionId() || '' },
    { key: '_client_ua', value: typeof navigator !== 'undefined' ? navigator.userAgent : '' },
    { key: '_client_ip', value: typeof localStorage !== 'undefined' ? (localStorage.getItem('_client_ip') || '') : '' },
    { key: '_client_ip_v6', value: typeof localStorage !== 'undefined' ? (localStorage.getItem('_client_ip_v6') || '') : '' },
    { key: '_traffic_source', value: getTrafficSource() },
  ];
}

// Sync tracking attributes to an existing cart
export async function syncTrackingAttributesToCart(cartId: string) {
  try {
    await storefrontApiRequest(CART_ATTRIBUTES_UPDATE, {
      cartId,
      attributes: buildTrackingCartAttributes(),
    });
  } catch (error) {
    console.error('Failed to sync tracking attributes to cart:', error);
  }
}

// ────────────────────────────────────────────────────────────────
// Shopify Cart helpers
// ────────────────────────────────────────────────────────────────

async function createShopifyCart(item: CartItem): Promise<{ cartId: string; checkoutUrl: string; lineId: string } | null> {
  const langState = useLanguage.getState();
  const displayCurrency = (langState.currency || 'USD') as Currency;
  const countryCode = langState.detectedCountry || CURRENCY_TO_COUNTRY[displayCurrency] || 'US';

  const data = await storefrontApiRequest(CART_CREATE_MUTATION, {
    input: { 
      lines: [{ quantity: item.quantity, merchandiseId: item.variantId }],
      attributes: buildTrackingCartAttributes(),
      buyerIdentity: { countryCode },
    },
  });

  if (data?.data?.cartCreate?.userErrors?.length > 0) {
    console.error('Cart creation failed:', data.data.cartCreate.userErrors);
    return null;
  }

  const cart = data?.data?.cartCreate?.cart;
  if (!cart?.checkoutUrl) return null;

  const lineId = cart.lines.edges[0]?.node?.id;
  if (!lineId) return null;

  return { cartId: cart.id, checkoutUrl: formatCheckoutUrl(cart.checkoutUrl), lineId };
}

async function addLineToShopifyCart(cartId: string, item: CartItem): Promise<{ success: boolean; lineId?: string; cartNotFound?: boolean }> {
  const data = await storefrontApiRequest(CART_LINES_ADD_MUTATION, {
    cartId,
    lines: [{ quantity: item.quantity, merchandiseId: item.variantId }],
  });

  const userErrors = data?.data?.cartLinesAdd?.userErrors || [];
  if (isCartNotFoundError(userErrors)) return { success: false, cartNotFound: true };
  if (userErrors.length > 0) {
    console.error('Add line failed:', userErrors);
    return { success: false };
  }

  const lines = data?.data?.cartLinesAdd?.cart?.lines?.edges || [];
  const newLine = lines.find((l: { node: { id: string; merchandise: { id: string } } }) => 
    l.node.merchandise.id === item.variantId
  );
  return { success: true, lineId: newLine?.node?.id };
}

// ────────────────────────────────────────────────────────────────
// Variant-based mutation: fetches ALL lineIds for a variant from
// Shopify, removes them all, and optionally re-adds a single line
// with the target quantity. This handles split lines from Buy X
// Get Y promotions.
// ────────────────────────────────────────────────────────────────

async function fetchCartLineIds(cartId: string): Promise<Array<{ lineId: string; variantId: string; quantity: number }> | null> {
  try {
    const data = await storefrontApiRequest(CART_QUERY, { id: cartId });
    const cart = data?.data?.cart;
    if (!cart) return null;
    const lines = cart.lines?.edges || [];
    return lines.map((e: any) => ({
      lineId: e.node.id,
      variantId: e.node.merchandise?.id || '',
      quantity: e.node.quantity,
    }));
  } catch {
    return null;
  }
}

async function setVariantQuantity(
  cartId: string,
  variantId: string,
  targetQty: number
): Promise<{ success: boolean; cartNotFound?: boolean }> {
  // 1) Fetch current lines for this variant from Shopify
  const allLines = await fetchCartLineIds(cartId);
  if (!allLines) return { success: false };

  const variantLines = allLines.filter(l => l.variantId === variantId);
  const lineIdsToRemove = variantLines.map(l => l.lineId);

  // 2) Remove ALL lines for this variant (handles split free-item lines)
  if (lineIdsToRemove.length > 0) {
    const removeData = await storefrontApiRequest(CART_LINES_REMOVE_MUTATION, {
      cartId,
      lineIds: lineIdsToRemove,
    });
    const removeErrors = removeData?.data?.cartLinesRemove?.userErrors || [];
    if (isCartNotFoundError(removeErrors)) return { success: false, cartNotFound: true };
    if (removeErrors.length > 0) {
      console.error('setVariantQuantity remove failed:', removeErrors);
      return { success: false };
    }
  }

  // 3) Re-add the variant as a single line if targetQty > 0
  if (targetQty > 0) {
    const addData = await storefrontApiRequest(CART_LINES_ADD_MUTATION, {
      cartId,
      lines: [{ quantity: targetQty, merchandiseId: variantId }],
    });
    const addErrors = addData?.data?.cartLinesAdd?.userErrors || [];
    if (isCartNotFoundError(addErrors)) return { success: false, cartNotFound: true };
    if (addErrors.length > 0) {
      console.error('setVariantQuantity re-add failed:', addErrors);
      return { success: false };
    }
  }

  return { success: true };
}

// ────────────────────────────────────────────────────────────────
// Pending re-sync mechanism — avoids stale data from race conditions
// ────────────────────────────────────────────────────────────────
let _needsResync = false;
let _trailingTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleSyncAfterMutation(syncFn: () => Promise<void>) {
  // Immediate sync
  syncFn();
  // Trailing sync (gives Shopify time to compute discounts)
  if (_trailingTimer) clearTimeout(_trailingTimer);
  _trailingTimer = setTimeout(() => syncFn(), 600);
}

// ────────────────────────────────────────────────────────────────
// Store
// ────────────────────────────────────────────────────────────────

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      cartId: null,
      checkoutUrl: null,
      discountCode: null,
      discountedTotal: null,
      discountedSubtotal: null,
      discountedVariantIds: [],
      isLoading: false,
      isSyncing: false,
      isCartOpen: false,

      addItem: async (item) => {
        const { items, cartId, clearCart } = get();
        const existingItem = items.find(i => i.variantId === item.variantId);
        
        set({ isLoading: true });
        try {
          // Track AddToCart event (Facebook + GA4)
          const displayCurrency = useLanguage.getState().currency || 'USD';
          const fixedP = getFixedPrice(item.product.node.handle, displayCurrency);
          const trackingData = {
            productId: item.product.node.id,
            variantId: item.variantId,
            title: item.product.node.title,
            price: fixedP ? fixedP.sale : parseFloat(item.price.amount),
            currency: fixedP ? displayCurrency : item.price.currencyCode,
            quantity: item.quantity,
          };
          const atcEventId = await generateEventId('AddToCart', `${trackingData.productId}_${Date.now()}`);
          await Promise.all([
            trackAddToCart(trackingData, atcEventId),
            trackGA4AddToCart(trackingData, atcEventId),
          ]);

          let addSuccess = false;

          if (!cartId) {
            const result = await createShopifyCart({ ...item, lineId: null });
            if (result) {
              set({
                cartId: result.cartId,
                checkoutUrl: result.checkoutUrl,
                items: [{ ...item, lineId: result.lineId }]
              });
              addSuccess = true;
            }
          } else if (existingItem) {
            // Use variant-based mutation to handle split lines
            const newQuantity = existingItem.quantity + item.quantity;
            const result = await setVariantQuantity(cartId, item.variantId, newQuantity);
            if (result.success) {
              const currentItems = get().items;
              set({ items: currentItems.map(i => i.variantId === item.variantId ? { ...i, quantity: newQuantity } : i) });
              syncTrackingAttributesToCart(cartId);
              addSuccess = true;
            } else if (result.cartNotFound) {
              clearCart();
            }
          } else {
            const result = await addLineToShopifyCart(cartId, { ...item, lineId: null });
            if (result.success) {
              const currentItems = get().items;
              set({ items: [...currentItems, { ...item, lineId: result.lineId ?? null }] });
              syncTrackingAttributesToCart(cartId);
              addSuccess = true;
            } else if (result.cartNotFound) {
              clearCart();
            }
          }

          // Auto-open cart drawer on successful add, then sync to detect auto-discounts
          if (addSuccess) {
            set({ isCartOpen: true });
            scheduleSyncAfterMutation(() => get().syncCart());
          }
        } catch (error) {
          console.error('Failed to add item:', error);
        } finally {
          set({ isLoading: false });
        }
      },

      updateQuantity: async (variantId, quantity) => {
        if (quantity <= 0) {
          await get().removeItem(variantId);
          return;
        }
        
        const { items, cartId, clearCart } = get();
        const item = items.find(i => i.variantId === variantId);
        if (!item || !cartId) return;

        set({ isLoading: true });
        try {
          // Use variant-based mutation: remove all split lines + re-add with target qty
          const result = await setVariantQuantity(cartId, variantId, quantity);
          if (result.success) {
            const currentItems = get().items;
            const updatedItems = currentItems.map(i => i.variantId === variantId ? { ...i, quantity } : i);
            const newTotalQty = updatedItems.reduce((sum, i) => sum + i.quantity, 0);
            const updates: Partial<CartStore> = { items: updatedItems };
            // Eagerly clear discount state when dropping below promo threshold
            if (newTotalQty < 3) {
              updates.discountedTotal = null;
              updates.discountedSubtotal = null;
              updates.discountedVariantIds = [];
            }
            set(updates as any);
            syncTrackingAttributesToCart(cartId);
            scheduleSyncAfterMutation(() => get().syncCart());
          } else if (result.cartNotFound) {
            clearCart();
          }
        } catch (error) {
          console.error('Failed to update quantity:', error);
        } finally {
          set({ isLoading: false });
        }
      },

      removeItem: async (variantId) => {
        const { items, cartId, clearCart } = get();
        if (!cartId) return;

        set({ isLoading: true });
        try {
          // Use variant-based mutation: removes ALL split lines for this variant
          const result = await setVariantQuantity(cartId, variantId, 0);
          if (result.success) {
            const currentItems = get().items;
            const newItems = currentItems.filter(i => i.variantId !== variantId);
            if (newItems.length === 0) {
              clearCart();
            } else {
              const newTotalQty = newItems.reduce((sum, i) => sum + i.quantity, 0);
              const updates: Partial<CartStore> = { items: newItems };
              if (newTotalQty < 3) {
                updates.discountedTotal = null;
                updates.discountedSubtotal = null;
                updates.discountedVariantIds = [];
              }
              set(updates as any);
              syncTrackingAttributesToCart(cartId);
              scheduleSyncAfterMutation(() => get().syncCart());
            }
          } else if (result.cartNotFound) {
            clearCart();
          }
        } catch (error) {
          console.error('Failed to remove item:', error);
        } finally {
          set({ isLoading: false });
        }
      },

      clearCart: () => set({ items: [], cartId: null, checkoutUrl: null, discountCode: null, discountedTotal: null, discountedSubtotal: null, discountedVariantIds: [] }),
      
      openCart: () => set({ isCartOpen: true }),
      
      closeCart: () => set({ isCartOpen: false }),
      
      getCheckoutUrl: () => get().checkoutUrl,
      
      getTotalItems: () => get().items.reduce((sum, item) => sum + item.quantity, 0),
      
      getTotalPrice: () => get().items.reduce((sum, item) => sum + (parseFloat(item.price.amount) * item.quantity), 0),

      applyDiscount: async (code: string) => {
        const { cartId } = get();
        if (!cartId) return false;

        set({ isLoading: true });
        try {
          const data = await storefrontApiRequest(CART_DISCOUNT_CODES_UPDATE, {
            cartId,
            discountCodes: [code],
          });

          const userErrors = data?.data?.cartDiscountCodesUpdate?.userErrors || [];
          if (userErrors.length > 0) {
            console.error('Apply discount failed:', userErrors);
            return false;
          }

          const appliedCodes = data?.data?.cartDiscountCodesUpdate?.cart?.discountCodes || [];
          const validCode = appliedCodes.find((c: { code: string; applicable: boolean }) => c.applicable);
          
          if (validCode) {
            const totalAmount = data?.data?.cartDiscountCodesUpdate?.cart?.cost?.totalAmount?.amount;
            const subtotalAmount = data?.data?.cartDiscountCodesUpdate?.cart?.cost?.subtotalAmount?.amount;
            set({ 
              discountCode: validCode.code,
              discountedTotal: totalAmount ? parseFloat(totalAmount) : null,
              discountedSubtotal: subtotalAmount ? parseFloat(subtotalAmount) : null,
            });
            return true;
          }
          return false;
        } catch (error) {
          console.error('Failed to apply discount:', error);
          return false;
        } finally {
          set({ isLoading: false });
        }
      },

      removeDiscount: async () => {
        const { cartId } = get();
        if (!cartId) return;

        set({ isLoading: true });
        try {
          await storefrontApiRequest(CART_DISCOUNT_CODES_UPDATE, {
            cartId,
            discountCodes: [],
          });
          set({ discountCode: null, discountedTotal: null, discountedSubtotal: null, discountedVariantIds: [] });
        } catch (error) {
          console.error('Failed to remove discount:', error);
        } finally {
          set({ isLoading: false });
        }
      },

      syncCart: async () => {
        const { cartId, isSyncing, clearCart } = get();
        if (!cartId) return;

        // Pending re-sync mechanism: if already syncing, mark for re-run
        if (isSyncing) {
          _needsResync = true;
          return;
        }

        set({ isSyncing: true });
        try {
          const data = await storefrontApiRequest(CART_QUERY, { id: cartId });
          if (!data) return;
          const cart = data?.data?.cart;
          if (!cart || cart.totalQuantity === 0) { clearCart(); return; }

          // ── Reconcile quantities from Shopify lines ──
          const lines = cart.lines?.edges || [];
          // Aggregate actual quantities per variant (handles split lines)
          const variantQuantities = new Map<string, number>();
          for (const edge of lines) {
            const vid = edge.node.merchandise?.id;
            if (vid) {
              variantQuantities.set(vid, (variantQuantities.get(vid) || 0) + edge.node.quantity);
            }
          }
          // Update local items to match Shopify's actual quantities
          const currentItems = get().items;
          const reconciledItems = currentItems
            .map(item => {
              const shopifyQty = variantQuantities.get(item.variantId);
              if (shopifyQty === undefined || shopifyQty <= 0) return null; // removed by Shopify
              return { ...item, quantity: shopifyQty };
            })
            .filter(Boolean) as CartItem[];

          // ── Detect line-level discounts (e.g. Buy X Get Y) ──
          let linesSubtotal = 0;
          let linesTotal = 0;
          const discountedVIds: string[] = [];

          for (const edge of lines) {
            const line = edge.node;
            const lineSub = parseFloat(line.cost?.subtotalAmount?.amount || '0');
            const lineTotal = parseFloat(line.cost?.totalAmount?.amount || '0');
            linesSubtotal += lineSub;
            linesTotal += lineTotal;
            if (lineSub > 0 && lineTotal < lineSub) {
              const variantId = line.merchandise?.id;
              if (variantId && !discountedVIds.includes(variantId)) {
                discountedVIds.push(variantId);
              }
            }
          }

          // Cart-level totals as fallback
          const cartSubtotal = parseFloat(cart.cost?.subtotalAmount?.amount || '0');
          const cartTotal = parseFloat(cart.cost?.totalAmount?.amount || '0');

          const effectiveSubtotal = linesSubtotal > 0 ? linesSubtotal : cartSubtotal;
          const effectiveTotal = linesTotal > 0 ? linesTotal : cartTotal;

          const stateUpdate: Partial<CartStore> = { items: reconciledItems };

          if (effectiveSubtotal > 0 && effectiveTotal < effectiveSubtotal) {
            stateUpdate.discountedTotal = effectiveTotal;
            stateUpdate.discountedSubtotal = effectiveSubtotal;
            stateUpdate.discountedVariantIds = discountedVIds;
          } else if (!get().discountCode) {
            // No manual coupon and no auto-discount — clear
            stateUpdate.discountedTotal = null;
            stateUpdate.discountedSubtotal = null;
            stateUpdate.discountedVariantIds = [];
          }

          set(stateUpdate as any);
        } catch (error) {
          console.error('Failed to sync cart with Shopify:', error);
        } finally {
          set({ isSyncing: false });
          // If another sync was requested while we were busy, run it now
          if (_needsResync) {
            _needsResync = false;
            get().syncCart();
          }
        }
      }
    }),
    {
      name: 'shopify-cart',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ 
        items: state.items, 
        cartId: state.cartId, 
        checkoutUrl: state.checkoutUrl,
        discountCode: state.discountCode,
        discountedTotal: state.discountedTotal,
        discountedSubtotal: state.discountedSubtotal,
        discountedVariantIds: state.discountedVariantIds,
      }),
    }
  )
);
